/**
 * 같은 origin의 /api/v1 호출. 인증은 HttpOnly 쿠키(__Host-budget_session)이고 토큰은 JS에서 볼 수 없다.
 * 쓰기 요청에는 세션에 묶인 CSRF 토큰을 X-CSRF-Token으로 보낸다. 브라우저 저장소에는 인증 정보를 두지 않는다.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
    readonly details: unknown = undefined
  ) {
    super(message);
  }
}

let csrfToken: string | null = null;
let onUnauthorized: () => void = () => undefined;

export const setCsrfToken = (token: string | null) => {
  csrfToken = token;
};
export const setUnauthorizedHandler = (handler: () => void) => {
  onUnauthorized = handler;
};

export interface RequestOptions {
  body?: unknown;
  /** 수정·삭제·복원: 현재 revision */
  ifMatch?: number;
  /** 같은 작업을 다시 보낼 때 같은 값을 써야 한 번만 저장된다. */
  idempotencyKey?: string;
  query?: Record<string, string | number | boolean | undefined | null>;
}

export async function request<T>(method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE', path: string, options: RequestOptions = {}): Promise<T> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(options.query ?? {})) if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken;
  if (options.ifMatch !== undefined) headers['If-Match'] = String(options.ifMatch);
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

  let response: Response;
  try {
    response = await fetch(`/api/v1${path}${params.size ? `?${params}` : ''}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: options.body === undefined ? undefined : JSON.stringify(options.body)
    });
  } catch {
    throw new ApiError(0, 'NETWORK', '서버에 연결하지 못했습니다. 네트워크를 확인하고 다시 시도해주세요.');
  }
  if (response.status === 204) return undefined as T;
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = payload?.error;
    if (response.status === 401 && path !== '/pairing/claim') onUnauthorized();
    throw new ApiError(response.status, error?.code ?? 'HTTP_ERROR', error?.message ?? `요청에 실패했습니다 (${response.status}).`, error?.fields ?? {}, error?.details);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'body'> = {}) => request<T>('POST', path, { ...options, body: body ?? {} }),
  patch: <T>(path: string, body: unknown, options: Omit<RequestOptions, 'body'> = {}) => request<T>('PATCH', path, { ...options, body }),
  put: <T>(path: string, body: unknown, options: Omit<RequestOptions, 'body'> = {}) => request<T>('PUT', path, { ...options, body }),
  delete: <T>(path: string, options: Omit<RequestOptions, 'body'> = {}) => request<T>('DELETE', path, options)
};

export const newId = () => crypto.randomUUID();
